import React, { useEffect, useState } from "react"
import { cn } from "@/lib/utils"
import MetricTooltip from "./metrics/MetricTooltip"
import MetricValue from "./metrics/MetricValue"
import MetricProgressBar from "./metrics/MetricProgressBar"
import MetricAnimations from "./metrics/MetricAnimations"

interface MetricCardProps {
  title: React.ReactNode
  value: string | number
  tooltip?: string
  min?: number
  max?: number
  current?: number
  progressColor?: string
  rightLabel?: string
  hideProgress?: boolean
  hideMinLabel?: boolean
  className?: string
  textColorClass?: string
  showZeroProgress?: boolean
  hideRightLabel?: boolean
  noData?: boolean
  alwaysShowValue?: boolean
  preserveHeight?: boolean // New prop to maintain consistent height when progress bar is hidden
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  tooltip,
  min = 0,
  max = 100,
  current = 0,
  progressColor,
  rightLabel,
  // TODO: make this false after fixing goals
  hideProgress = true,
  hideMinLabel = false,
  className,
  textColorClass = "",
  showZeroProgress = false,
  // TODO: make this false after fixing goals
  hideRightLabel = true,
  noData = false,
  alwaysShowValue = false,
  preserveHeight = false, // Default is false for backward compatibility
}) => {
  const [animatedProgress, setAnimatedProgress] = useState(0)
  const progress = Math.min((current / max) * 100, 100)
  const [isComplete, setIsComplete] = useState(false)

  // If there's no data and this metric isn't set to always show, display a dash
  const shouldShowNoData = noData && !alwaysShowValue
  console.log("Should show no data:", shouldShowNoData)

  useEffect(() => {
    setAnimatedProgress(0)
    const timer = setTimeout(() => {
      setAnimatedProgress(progress)
    }, 50)
    return () => clearTimeout(timer)
  }, [progress])

  useEffect(() => {
    setIsComplete(progress >= 100)
  }, [progress])

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 flex flex-col transition-all duration-300 relative">
      {tooltip && <MetricTooltip tooltip={tooltip} />}

      <MetricValue
        value={value}
        title={title}
        textColorClass={textColorClass}
        noData={shouldShowNoData}
      />

      <div className="w-full mt-auto">
        {hideProgress || shouldShowNoData ? (
          <div
            className={cn(
              "text-xs text-gray-500 text-center",
              preserveHeight ? "h-7" : "h-4"
            )}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {rightLabel && !shouldShowNoData && <span>{rightLabel}</span>}
          </div>
        ) : (
          <MetricProgressBar
            min={min}
            max={max}
            current={current}
            animatedProgress={animatedProgress}
            isComplete={isComplete}
            progressColor={progressColor}
            showZeroProgress={showZeroProgress}
            hideMinLabel={hideMinLabel}
            rightLabel={rightLabel}
            className={className}
            hideRightLabel={hideRightLabel}
          />
        )}
      </div>

      <MetricAnimations />
    </div>
  )
}

export default MetricCard
